class BannerSlideshow {
  constructor(element) {
    this.container = element;
    this.sectionId = element.dataset.sectionId;
    this.slides = element.querySelectorAll('.slide');
    this.navPrev = element.querySelector('.slideshow-nav-prev');
    this.navNext = element.querySelector('.slideshow-nav-next');
    this.dots = element.querySelectorAll('.slideshow-dot');
    this.autoplay = element.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(element.dataset.autoplaySpeed) || 5000;
    this.showNavigation = element.dataset.showNavigation === 'true';
    this.showDots = element.dataset.showDots === 'true';
    
    this.currentSlide = 0;
    this.autoplayTimer = null;
    this.isTransitioning = false;
    
    this.init();
  }
  
  init() {
    if (this.slides.length <= 1) return;
    
    // Set initial slide
    this.showSlide(0);
    
    // Setup navigation
    if (this.showNavigation) {
      this.navPrev?.addEventListener('click', () => this.prevSlide());
      this.navNext?.addEventListener('click', () => this.nextSlide());
    }
    
    // Setup dots
    if (this.showDots) {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index));
      });
    }
    
    // Setup autoplay
    if (this.autoplay) {
      this.startAutoplay();
      
      // Pause on hover
      this.container.addEventListener('mouseenter', () => this.stopAutoplay());
      this.container.addEventListener('mouseleave', () => this.startAutoplay());
      
      // Pause on focus
      this.container.addEventListener('focusin', () => this.stopAutoplay());
      this.container.addEventListener('focusout', () => this.startAutoplay());
    }
    
    // Keyboard navigation
    this.container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (e.key === 'ArrowRight') {
        this.nextSlide();
      } else if (e.key === 'Home') {
        e.preventDefault();
        this.goToSlide(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        this.goToSlide(this.slides.length - 1);
      }
    });
    
    // Touch/swipe support
    this.setupTouchEvents();
    
    // Pause autoplay when page is not visible
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else if (this.autoplay) {
        this.startAutoplay();
      }
    });
  }
  
  showSlide(index) {
    if (this.isTransitioning || index === this.currentSlide) return;
    
    this.isTransitioning = true;
    
    // Hide current slide
    this.slides[this.currentSlide].classList.remove('active');
    
    // Show new slide
    this.slides[index].classList.add('active');
    
    // Update dots
    if (this.showDots) {
      this.dots[this.currentSlide].classList.remove('active');
      this.dots[index].classList.add('active');
    }
    
    this.currentSlide = index;
    
    // Reset transition flag after animation completes
    setTimeout(() => {
      this.isTransitioning = false;
    }, 500);
  }
  
  nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.slides.length;
    this.showSlide(nextIndex);
  }
  
  prevSlide() {
    const prevIndex = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    this.showSlide(prevIndex);
  }
  
  goToSlide(index) {
    if (index >= 0 && index < this.slides.length) {
      this.showSlide(index);
    }
  }
  
  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }
  
  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
  
  setupTouchEvents() {
    let startX = 0;
    let startY = 0;
    let endX = 0;
    let endY = 0;
    let isDragging = false;
    
    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
    };
    
    const handleTouchMove = (e) => {
      if (!isDragging) return;
      endX = e.touches[0].clientX;
      endY = e.touches[0].clientY;
    };
    
    const handleTouchEnd = () => {
      if (!isDragging) return;
      
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const minSwipeDistance = 50;
      
      // Check if it's a horizontal swipe and not a vertical scroll
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
        if (deltaX > 0) {
          this.prevSlide();
        } else {
          this.nextSlide();
        }
      }
      
      isDragging = false;
      startX = 0;
      startY = 0;
      endX = 0;
      endY = 0;
    };
    
    this.container.addEventListener('touchstart', handleTouchStart, { passive: true });
    this.container.addEventListener('touchmove', handleTouchMove, { passive: true });
    this.container.addEventListener('touchend', handleTouchEnd, { passive: true });
  }
  
  destroy() {
    this.stopAutoplay();
    
    // Remove event listeners
    if (this.showNavigation) {
      this.navPrev?.removeEventListener('click', this.prevSlide);
      this.navNext?.removeEventListener('click', this.nextSlide);
    }
    
    if (this.showDots) {
      this.dots.forEach((dot, index) => {
        dot.removeEventListener('click', () => this.goToSlide(index));
      });
    }
    
    this.container.removeEventListener('mouseenter', this.stopAutoplay);
    this.container.removeEventListener('mouseleave', this.startAutoplay);
    this.container.removeEventListener('focusin', this.stopAutoplay);
    this.container.removeEventListener('focusout', this.startAutoplay);
    this.container.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }
}

// Initialize slideshops when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const slideshops = document.querySelectorAll('.banner-slideshow');
  slideshops.forEach(slideshow => new BannerSlideshow(slideshow));
});

// For theme editor support
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    const target = e.target.querySelector('.banner-slideshow');
    if (target) {
      new BannerSlideshow(target);
    }
  });
  
  document.addEventListener('shopify:section:unload', (e) => {
    const target = e.target.querySelector('.banner-slideshow');
    if (target && target.slideshow) {
      target.slideshow.destroy();
    }
  });
}
